import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useLexicalEditable} from '@lexical/react/useLexicalEditable';
import type {TableCellNode, TableDOMCell, TableMapType} from '@lexical/table';
import {
  $computeTableMapSkipCellCheck,
  $getTableNodeFromLexicalNodeOrThrow,
  $isTableCellNode,
  $isTableNode,
  getDOMCellFromTarget,
  getTableElement,
  TableNode,
} from '@lexical/table';
import {calculateZoomLevel, mergeRegister} from '@lexical/utils';
import {getPlanningBreakoutStyle} from 'components/DSYS/editor/planningTableLayout';
import {usePlanningTableReserveRight} from 'components/DSYS/editor/PlanningTableReserveContext';
import {BUTTON_SIZE} from 'components/DSYS/editor/plugins/tableButtons/utils';
import tableStyles from 'components/DSYS/editor/themes/TableTheme.module.css';
import type {LexicalEditor, NodeKey} from 'lexical';
import {
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  isHTMLElement,
  SKIP_SCROLL_INTO_VIEW_TAG,
} from 'lexical';
import type {JSX} from 'react';
import {
  CSSProperties,
  PointerEventHandler,
  ReactPortal,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';

import resizerStylesCss from './TableCellResizer.module.css';

const MIN_COLUMN_WIDTH = 90;
const WIDTH_STYLE_REGEX = /width:\s*([\d.]+)px/;

const findVisibleRowBounds = (
  rows: NodeListOf<HTMLTableRowElement>
): {firstRow: DOMRect | null; lastRow: DOMRect | null} => {
  let firstRow: DOMRect | null = null;
  let lastRow: DOMRect | null = null;

  rows.forEach((row) => {
    const rect = row.getBoundingClientRect();
    if (rect.height !== 0) {
      if (!firstRow) {
        firstRow = rect;
      }
      lastRow = rect;
    }
  });

  return {firstRow, lastRow};
};

// Helper function to get the editor container width
const getEditorWidth = (editor: LexicalEditor): number => {
  const editorElement = editor.getRootElement();
  const editorRect = editorElement?.getBoundingClientRect();
  return editorRect ? editorRect.width : 754; // Fallback to 754 if editor not found
};

const getStyledTableWidth = (tableNode: TableNode): number | null => {
  const widthMatch = tableNode.getStyle().match(WIDTH_STYLE_REGEX);
  if (!widthMatch) {
    return null;
  }

  const width = Number.parseFloat(widthMatch[1]);
  return Number.isFinite(width) ? width : null;
};

const syncColWidthsToStyledWidth = (
  colWidths: readonly number[],
  styledWidth: number
): number[] => {
  const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
  if (totalWidth <= 0 || Math.abs(totalWidth - styledWidth) < 1) {
    return [...colWidths];
  }

  const scale = styledWidth / totalWidth;
  const scaledWidths = colWidths.map((width) => width * scale);
  const scaledWidthSum = scaledWidths.reduce((sum, width) => sum + width, 0);

  if (scaledWidths.length > 0) {
    scaledWidths[scaledWidths.length - 1] += styledWidth - scaledWidthSum;
  }

  return scaledWidths;
};

const maybeSyncTableColWidths = (tableNode: TableNode): number[] | null => {
  const colWidths = tableNode.getColWidths();
  if (!colWidths || colWidths.length !== tableNode.getColumnCount()) {
    return null;
  }

  const styledWidth = getStyledTableWidth(tableNode);
  if (styledWidth == null) {
    return null;
  }

  const syncedColWidths = syncColWidthsToStyledWidth(colWidths, styledWidth);
  const hasChanged = syncedColWidths.some((width, index) => Math.abs(width - colWidths[index]) > 1);
  return hasChanged ? syncedColWidths : null;
};

// Apply table positioning to DOM only
// This function must be called within an editor.read() or editor.update() context
export const updateTablePositioning = (
  tableNode: TableNode,
  editor: LexicalEditor,
  desiredReserveRightPx = 0
) => {
  const colWidths = tableNode.getColWidths();
  if (!colWidths) return;

  const tableKey = tableNode.getKey();

  // Schedule DOM update after current update cycle
  queueMicrotask(() => {
    const wrapperElement = editor.getElementByKey(tableKey);
    if (!wrapperElement) return;

    const editorElement = editor.getRootElement();
    if (!editorElement) return;

    const editorRect = editorElement.getBoundingClientRect();
    const tableWidthPx = colWidths.reduce((sum, width) => sum + width, 0);

    const buttonOffset = BUTTON_SIZE + 1;
    const {widthPx, marginLeftPx} = getPlanningBreakoutStyle({
      windowWidth: window.innerWidth,
      editorWidth: editorRect.width,
      buttonOffset,
      desiredReserveRightPx,
      tableWidthPx,
    });
    const handleBleedPx = BUTTON_SIZE;
    wrapperElement.style.width = `${widthPx}px`;
    wrapperElement.style.marginLeft = `${marginLeftPx - handleBleedPx}px`;
    wrapperElement.style.marginRight = `${-handleBleedPx}px`;
  });
};

const TableCellResizer = ({editor}: {editor: LexicalEditor}): JSX.Element => {
  const isEditable = useLexicalEditable();
  const targetRef = useRef<HTMLElement | null>(null);
  const resizerRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line deprecation/deprecation
  const tableRectRef = useRef<ClientRect | null>(null);
  const [hasTable, setHasTable] = useState(false);

  const pointerStartXRef = useRef<number | null>(null);
  const [pointerCurrentX, setPointerCurrentX] = useState<number | null>(null);
  const lastAppliedChangeRef = useRef<number>(0);

  const [activeCell, updateActiveCell] = useState<TableDOMCell | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [scrollTrigger, setScrollTrigger] = useState<number>(0);

  const tableKeysRef = useRef(new Set<NodeKey>());

  const reserveRightPx = usePlanningTableReserveRight();
  const reserveRightRef = useRef(reserveRightPx);
  reserveRightRef.current = reserveRightPx;

  const resetState = useCallback(() => {
    updateActiveCell(null);
    targetRef.current = null;
    setIsDragging(false);
    pointerStartXRef.current = null;
    tableRectRef.current = null;
    lastAppliedChangeRef.current = 0;
  }, []);

  useEffect(() => {
    const tableKeys = tableKeysRef.current;
    return mergeRegister(
      editor.registerMutationListener(TableNode, (nodeMutations) => {
        for (const [nodeKey, mutation] of nodeMutations) {
          if (mutation === 'destroyed') {
            tableKeys.delete(nodeKey);
          } else {
            tableKeys.add(nodeKey);

            // Apply local positioning when table is created/updated (including from collab sync)
            editor.read(() => {
              const node = $getNodeByKey(nodeKey);
              if ($isTableNode(node)) {
                updateTablePositioning(node, editor, reserveRightRef.current);
              }
            });
          }
        }
        setHasTable(tableKeys.size > 0);
      }),
      editor.registerNodeTransform(TableNode, (tableNode) => {
        const syncedColWidths = maybeSyncTableColWidths(tableNode);
        if (syncedColWidths) {
          tableNode.setColWidths(syncedColWidths);
          updateTablePositioning(tableNode, editor, reserveRightRef.current);
          return tableNode;
        }

        if (tableNode.getColWidths()) {
          return tableNode;
        }

        // Check if the table has preserved column widths from paste operation
        const pastedTableElement = editor.getElementByKey(tableNode.getKey());
        if (pastedTableElement) {
          const columnWidthsData = pastedTableElement.getAttribute('data-column-widths');
          if (columnWidthsData) {
            try {
              const columnWidths = JSON.parse(columnWidthsData);
              const numColumns = tableNode.getColumnCount();

              if (columnWidths.length === numColumns) {
                tableNode.setColWidths(columnWidths);
                const totalWidth = columnWidths.reduce((sum: number, w: number) => sum + w, 0);
                tableNode.setStyle(`width: ${totalWidth}px;`);

                // Update table positioning (applied to DOM only, not synced)
                updateTablePositioning(tableNode, editor, reserveRightRef.current);

                // Remove the data attribute after using it
                pastedTableElement.removeAttribute('data-column-widths');
                return tableNode;
              }
            } catch (e) {
              console.log('TableCellResizer: Error parsing column widths:', e);
              // If parsing fails, fall through to default behavior
            }
          }
        }

        // Only apply default column widths if the table doesn't have any style width set
        // This prevents overriding widths from pasted tables
        const currentStyle = tableNode.getStyle();
        if (currentStyle && currentStyle.includes('width:')) {
          return tableNode;
        }

        const numColumns = tableNode.getColumnCount();
        // Use editor width as the minimum table width
        const editorWidth = getEditorWidth(editor);
        const colWidths = Array(numColumns).fill(editorWidth / numColumns);
        tableNode.setColWidths(colWidths);

        // Set initial table width
        const totalWidth = colWidths.reduce((sum, w) => sum + w, 0);
        tableNode.setStyle(`width: ${totalWidth}px;`);

        // Update table positioning (applied to DOM only, not synced)
        updateTablePositioning(tableNode, editor, reserveRightRef.current);

        return tableNode;
      })
    );
  }, [editor]);

  useEffect(() => {
    if (!hasTable) {
      return;
    }

    editor.read(() => {
      for (const key of tableKeysRef.current) {
        const node = $getNodeByKey(key);
        if ($isTableNode(node)) {
          updateTablePositioning(node, editor, reserveRightPx);
        }
      }
    });
  }, [editor, hasTable, reserveRightPx]);

  // Handle window resize to update table positioning with debouncing
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      if (!hasTable) return;

      // Clear previous timeout
      clearTimeout(resizeTimeout);

      // Debounce resize events to prevent excessive updates
      resizeTimeout = setTimeout(() => {
        editor.read(() => {
          for (const key of tableKeysRef.current) {
            const node = $getNodeByKey(key);
            if ($isTableNode(node)) {
              updateTablePositioning(node, editor, reserveRightRef.current);
            }
          }
        });
      }, 16); // ~60fps debouncing
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [hasTable, editor]);

  // Handle table wrapper scroll to update resizer position
  useEffect(() => {
    if (!hasTable) {
      return;
    }

    const handleScroll = () => {
      // Trigger re-render to update resizer position
      setScrollTrigger((prev) => prev + 1);
    };

    // Find all table wrapper elements and attach scroll listeners

    const tableWrappers = document.querySelectorAll('.' + tableStyles.tableScrollableWrapper);
    tableWrappers.forEach((wrapper) => {
      wrapper.addEventListener('scroll', handleScroll, {passive: true});
    });

    return () => {
      tableWrappers.forEach((wrapper) => {
        wrapper.removeEventListener('scroll', handleScroll);
      });
    };
  }, [hasTable]);

  const getCellColumnIndex = (tableCellNode: TableCellNode, tableMap: TableMapType) => {
    for (let row = 0; row < tableMap.length; row++) {
      for (let column = 0; column < tableMap[row].length; column++) {
        if (tableMap[row][column].cell === tableCellNode) {
          return column;
        }
      }
    }
  };

  useEffect(() => {
    if (!isEditable) {
      return;
    }

    if (!hasTable) {
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const target = event.target;
      if (!isHTMLElement(target)) {
        return;
      }

      if (isDragging) {
        event.preventDefault();
        event.stopPropagation();
        setPointerCurrentX(event.clientX);
        return;
      }
      if (resizerRef.current && resizerRef.current.contains(target)) {
        return;
      }

      if (targetRef.current !== target) {
        targetRef.current = target;
        const cell = getDOMCellFromTarget(target);

        if (cell && activeCell !== cell) {
          editor.getEditorState().read(
            () => {
              const tableCellNode = $getNearestNodeFromDOMNode(cell.elem);
              if (!tableCellNode) {
                // This can happen if the DOM node doesn't have a corresponding Lexical node yet
                // (e.g., when content is loaded from Yjs). Just return silently.
                console.warn('TableCellResizer: Table cell node not found for DOM element');
                return;
              }

              const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
              const tableElement = getTableElement(
                tableNode,
                editor.getElementByKey(tableNode.getKey())
              );

              if (!tableElement) {
                throw new Error('TableCellResizer: Table element not found.');
              }

              targetRef.current = target;
              // Get the full table bounds including header and body
              const tableRect = tableElement.getBoundingClientRect();

              // Also get all table rows to ensure we cover the full height
              const allRows = tableElement.querySelectorAll('tr');
              const {firstRow, lastRow} = findVisibleRowBounds(allRows);
              if (firstRow && lastRow) {
                const fullHeight = lastRow.bottom - firstRow.top;

                // Update the table rect to use the full height of all rows
                tableRectRef.current = {
                  ...tableRect,
                  height: fullHeight,
                  top: firstRow.top,
                  bottom: lastRow.bottom,
                };
              } else {
                tableRectRef.current = tableRect;
              }

              updateActiveCell(cell);
            },
            {editor}
          );
        } else if (cell == null) {
          resetState();
        }
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        onPointerMove(event);
      }
    };

    // Register pointer events on document to ensure dragging works even when mouse moves outside editor bounds
    document.addEventListener('pointermove', onPointerMove, {capture: true});
    document.addEventListener('pointerdown', onPointerDown, {capture: true});

    return () => {
      document.removeEventListener('pointermove', onPointerMove, {capture: true});
      document.removeEventListener('pointerdown', onPointerDown, {capture: true});
    };
  }, [activeCell, isEditable, isDragging, editor, resetState, hasTable]);

  const updateColumnWidth = useCallback(
    (widthChange: number) => {
      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }

      // Use a more immediate update approach
      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
          if (!$isTableCellNode(tableCellNode)) {
            throw new Error('TableCellResizer: Table cell node not found.');
          }

          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
          const columnIndex = getCellColumnIndex(tableCellNode, tableMap);
          if (columnIndex === undefined) {
            throw new Error('TableCellResizer: Table column not found.');
          }

          const syncedColWidths = maybeSyncTableColWidths(tableNode);
          if (syncedColWidths) {
            tableNode.setColWidths(syncedColWidths);
          }

          const colWidths = syncedColWidths ?? tableNode.getColWidths();
          if (!colWidths) {
            return;
          }
          const width = colWidths[columnIndex];
          if (width === undefined) {
            return;
          }

          const newColWidths = [...colWidths];
          const newWidth = Math.max(width + widthChange, MIN_COLUMN_WIDTH);

          // Only change the width of the column being resized
          newColWidths[columnIndex] = newWidth;

          // Calculate the new total table width
          const totalWidth = newColWidths.reduce((sum, w) => sum + w, 0);

          // Enforce minimum width equal to editor width
          const editorWidth = getEditorWidth(editor);
          if (totalWidth < editorWidth) {
            // Don't allow the table to shrink below editor width
            return;
          }

          tableNode.setColWidths(newColWidths);
          tableNode.setStyle(`width: ${totalWidth}px;`);
          updateTablePositioning(tableNode, editor, reserveRightRef.current);
        },
        {tag: SKIP_SCROLL_INTO_VIEW_TAG}
      );

      // Update table rect after the DOM has been updated to reflect new table height
      // This ensures the resizer line matches the current table dimensions
      setTimeout(() => {
        if (!activeCell) return;

        editor.getEditorState().read(
          () => {
            const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
            if (!tableCellNode) return;

            const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
            const tableElement = getTableElement(
              tableNode,
              editor.getElementByKey(tableNode.getKey())
            );

            if (!tableElement) return;

            // Get updated table bounds
            const tableRect = tableElement.getBoundingClientRect();

            // Get all table rows to ensure we cover the full height
            const allRows = tableElement.querySelectorAll('tr');
            const {firstRow, lastRow} = findVisibleRowBounds(allRows);
            if (firstRow && lastRow) {
              const fullHeight = lastRow.bottom - firstRow.top;

              // Update the table rect to use the full height of all rows
              tableRectRef.current = {
                ...tableRect,
                height: fullHeight,
                top: firstRow.top,
                bottom: lastRow.bottom,
              };
            } else {
              tableRectRef.current = tableRect;
            }
          },
          {editor}
        );
      }, 0);
    },
    [activeCell, editor]
  );

  const pointerUpHandler = useCallback(() => {
    const handler = (event: PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }

      // No need to update size here since we're doing it in real-time during drag
      resetState();
      document.removeEventListener('pointerup', handler);
    };
    return handler;
  }, [activeCell, resetState]);

  const toggleResize = useCallback(
    (): PointerEventHandler<HTMLDivElement> => (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }

      pointerStartXRef.current = event.clientX;
      setPointerCurrentX(pointerStartXRef.current);
      setIsDragging(true);

      document.addEventListener('pointerup', pointerUpHandler());
    },
    [activeCell, pointerUpHandler]
  );

  // Real-time updates during drag
  useEffect(() => {
    if (!isDragging || !pointerCurrentX || !pointerStartXRef.current || !activeCell) {
      return;
    }

    const startX = pointerStartXRef.current;
    const zoom = calculateZoomLevel(activeCell.elem);

    const totalWidthChange = (pointerCurrentX - startX) / zoom;
    const incrementalWidthChange = totalWidthChange - lastAppliedChangeRef.current;
    if (Math.abs(incrementalWidthChange) > 0.1) {
      // Update more frequently for better responsiveness
      updateColumnWidth(incrementalWidthChange);
      lastAppliedChangeRef.current = totalWidthChange;
    }
  }, [isDragging, pointerCurrentX, activeCell, updateColumnWidth]);

  const getResizerStyle = useCallback((): CSSProperties | null => {
    if (!activeCell) {
      return null;
    }

    // Always get fresh bounding rect to account for table repositioning
    const {height, width, top, left} = activeCell.elem.getBoundingClientRect();
    const zoneWidth = 16; // Pixel width of the zone where you can drag the edge

    const tableRect = tableRectRef.current;

    // Show full column line when dragging or when hovering near the right edge
    if (tableRect) {
      const currentX = window.scrollX + left + width - 5;

      return {
        cursor: 'col-resize',
        top: `${window.scrollY + tableRect.top}px`,
        left: `${currentX}px`,
        width: '10px',
        height: `${tableRect.height}px`,
        mixBlendMode: 'unset',
        opacity: isDragging ? 1 : 0.6,
        zIndex: 9,
      };
    }

    // Only show the invisible drag zone when not showing the column line
    return {
      backgroundColor: 'transparent',
      cursor: 'col-resize',
      height: `${height}px`,
      left: `${window.scrollX + left + width - zoneWidth / 2}px`,
      top: `${window.scrollY + top}px`,
      width: `${zoneWidth}px`,
    };

    // scrollTrigger changes on scroll, which indicates that window.scrollX or
    // window.scrollY probably changed.
    // But adding those directly won't trigger a re-render automatically.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCell, isDragging, scrollTrigger]);

  const resizerStyle = getResizerStyle();

  return (
    <div ref={resizerRef}>
      {activeCell != null && resizerStyle && (
        <div
          className={resizerStylesCss.resizer}
          style={resizerStyle}
          onPointerDown={toggleResize()}
        />
      )}
    </div>
  );
};

export default function TableCellResizerPlugin(): null | ReactPortal {
  const [editor] = useLexicalComposerContext();

  return useMemo(() => createPortal(<TableCellResizer editor={editor} />, document.body), [editor]);
}
